import Decimal from 'decimal.js';

export const CENTS_DECIMAL_PLACES = 2;
export const CENTS_ROUNDING = Decimal.ROUND_HALF_UP;

/**
 * Round a monetary amount to cents as a `Decimal`, the way the backend's `to_cents` does —
 * half away from zero on the DECIMAL literal, not on the binary double — so a client-side
 * total or gate can never disagree with the server by a cent.
 *
 * `decimal.js` is the frontend's counterpart to Python's `decimal`: its constructor takes a
 * `number` by that number's shortest round-trip string, exactly as `to_cents` does with
 * `Decimal(str(value))`, and `ROUND_HALF_UP` means "away from zero when equidistant" on both
 * sides.
 *
 * `Math.round(value * 100) / 100` is NOT equivalent: the `* 100` reintroduces binary error
 * (`261.405 * 100 === 26140.499999999996`, rounding down where the backend rounds up) and
 * `Math.round` sends negative halves toward +Infinity (`-1.125 -> -1.12`) instead of away
 * from zero, which is the wrong direction for refund rows.
 *
 * Prefer this over `toCents` when the result feeds further arithmetic: staying in `Decimal`
 * until the final `toNumber()` keeps every intermediate term exact, so the chain matches the
 * backend's `Decimal` arithmetic digit for digit instead of picking up binary residue between
 * steps (`117.75 + 143.66 === 261.40999999999997`).
 *
 * LOCKSTEP CONTRACT: this is the one-to-one mirror of `server/base_utils.py::to_cents`.
 * Any change to either side's semantics (rounding mode, precision, input coercion) MUST be
 * applied to the other side — and to the vectors in `toCents.test.ts` — in the same change,
 * or client-side totals and reserve/limit gates will drift a cent from the server.
 */
export const toCentsDecimal = (value: number | null | undefined): Decimal =>
  new Decimal(value || 0).toDecimalPlaces(CENTS_DECIMAL_PLACES, CENTS_ROUNDING);

/**
 * `toCentsDecimal` as a plain number, for comparisons and display values that do no further
 * arithmetic. Carries the same lockstep contract with `to_cents`.
 */
const toCents = (value: number): number => toCentsDecimal(value).toNumber();

/**
 * Multiply a monetary amount by a rate and round the *decimal* product to cents, matching the
 * backend's `(Decimal(amount) * Decimal(rate)).quantize(0.01, ROUND_HALF_UP)` exactly.
 *
 * Rounding the IEEE-754 product instead loses the half cent before it can be rounded:
 * `100.10 * 0.75` is exactly `75.075` (-> 75.08) but evaluates to `75.07499999999999`, which
 * `toCents` sends down to 75.07. `Decimal.times` keeps the product exact — at any rate
 * precision, not just the four decimals a co-insurance rate happens to carry — so the half
 * cent survives to the rounding step.
 */
export const multiplyToCentsDecimal = (amount: Decimal.Value, rate: Decimal.Value): Decimal =>
  new Decimal(amount || 0).times(rate || 0).toDecimalPlaces(CENTS_DECIMAL_PLACES, CENTS_ROUNDING);

/** `multiplyToCentsDecimal` as a plain number, for values that do no further arithmetic. */
export const multiplyToCents = (amount: number, rate: number): number =>
  multiplyToCentsDecimal(amount, rate).toNumber();

export default toCents;
